import { motion } from 'motion/react';
import { Card } from './ui/card';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

type HumidityPoint = {
  time: string;
  aht_hum: number | null | undefined;
  soil_moisture?: number | null | undefined;
};

export interface HumidityChartProps {
  data: HumidityPoint[];
}

const toNum = (v: any): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

export function HumidityChart({ data }: HumidityChartProps) {
  const formattedData = (data ?? []).map((item) => ({
    // 12:34 format czasu
    time: new Date(item.time).toLocaleTimeString('pl-PL', {
      hour: '2-digit',
      minute: '2-digit',
    }),
    // wewnętrzne klucze bez spacji – łatwiejsze dla TS/Recharts
    air: toNum(item.aht_hum),
    soil: toNum(item.soil_moisture),
  }));

  const hasAnyPoint = formattedData.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
    >
      <Card className="p-6">
        <h3 className="mb-4">Wilgotność — ostatnie 24h</h3>

        {!hasAnyPoint ? (
          <div className="h-[300px] flex items-center justify-center text-gray-500 text-sm">
            Brak danych do wyświetlenia
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={formattedData}>
              <defs>
                <linearGradient id="colorHumidity" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorSoil" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="time" stroke="#6b7280" style={{ fontSize: '12px' }} />
              <YAxis
                stroke="#6b7280"
                style={{ fontSize: '12px' }}
                label={{ value: '%', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                }}
                formatter={(value: any, name: any) => {
                  const n = Number(value);
                  return [Number.isFinite(n) ? `${n.toFixed(2)}%` : '—', name];
                }}
              />

              {/* Powietrze */}
              <Area
                type="monotone"
                dataKey="air"
                name="Wilgotność powietrza"
                stroke="#3b82f6"
                fillOpacity={1}
                fill="url(#colorHumidity)"
                animationDuration={2000}
                connectNulls
              />

              {/* Gleba – jeśli jest */}
              <Area
                type="monotone"
                dataKey="soil"
                name="Wilgotność gleby"
                stroke="#8b5cf6"
                fillOpacity={1}
                fill="url(#colorSoil)"
                animationDuration={2000}
                animationBegin={300}
                connectNulls
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>
    </motion.div>
  );
}
